import { createElement, Fragment, type ReactNode } from 'react';

const ALLOWED_TAGS = new Set([
  'A',
  'B',
  'STRONG',
  'EM',
  'I',
  'U',
  'BR',
  'P',
  'SPAN',
]);

function isSafeHref(href: string): boolean {
  const trimmed = href.trim();
  if (!trimmed) return false;
  try {
    const url = new URL(trimmed, 'https://example.invalid');
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
}

function convertChildren(element: Element): ReactNode[] {
  return Array.from(element.childNodes).map((child, index) =>
    convertNode(child, index)
  );
}

function convertNode(node: ChildNode, key: number): ReactNode {
  if (node.nodeType === Node.TEXT_NODE) {
    return node.textContent ?? '';
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = node as Element;
  const tag = element.tagName.toUpperCase();

  if (!ALLOWED_TAGS.has(tag)) {
    return createElement(Fragment, { key }, convertChildren(element));
  }

  const children = convertChildren(element);

  if (tag === 'BR') {
    return createElement('br', { key });
  }

  if (tag === 'A') {
    const href = element.getAttribute('href') ?? '';
    if (!isSafeHref(href)) {
      return createElement(Fragment, { key }, children);
    }
    return createElement(
      'a',
      {
        key,
        href,
        target: '_blank',
        rel: 'noopener noreferrer',
      },
      children
    );
  }

  return createElement(tag.toLowerCase(), { key }, children);
}

export function renderTrustedHtml(html: string): ReactNode {
  if (!html) return null;

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${html}</div>`, 'text/html');
  const root = doc.body.firstElementChild;
  if (!root) return html;

  return Array.from(root.childNodes).map((child, index) =>
    convertNode(child, index)
  );
}
